import { useEffect } from "react";
import { SITE_URL, AUTHOR, ORGANIZATION } from "../../data/journalPosts";

/**
 * JournalSeo — declaratively manages <head> tags for journal pages.
 *
 * - Sets <title>, meta description, canonical, OG, twitter:card
 * - Injects JSON-LD blocks (Article, FAQPage, BreadcrumbList) tagged
 *   with a data attribute so we can clean them up on unmount /
 *   navigation. No external deps; no React Helmet.
 */
const TAG_ATTR = "data-am-journal-seo";

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    el.setAttribute(TAG_ATTR, "1");
    document.head.appendChild(el);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    if (!el.getAttribute(TAG_ATTR)) el.setAttribute(TAG_ATTR, "1");
  }
};

const upsertLink = (rel, href) => {
  // Drop any non-owned <link rel=...> first so the page renders with
  // a single canonical instead of doubling up with the index.html base.
  document.head.querySelectorAll(`link[rel="${rel}"]`).forEach((n) => {
    if (!n.getAttribute(TAG_ATTR)) n.remove();
  });
  let el = document.head.querySelector(`link[rel="${rel}"][${TAG_ATTR}]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    el.setAttribute(TAG_ATTR, "1");
    document.head.appendChild(el);
  }
  el.href = href;
};

const injectJsonLd = (id, payload) => {
  let el = document.head.querySelector(`script[data-am-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-am-jsonld", id);
    el.setAttribute(TAG_ATTR, "1");
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
};

const cleanup = () => {
  document.head.querySelectorAll(`[${TAG_ATTR}]`).forEach((n) => n.remove());
  // Restore a default homepage canonical when the journal page tears
  // down so SPA navigation never lands at "no canonical".
  if (document.head.querySelectorAll('link[rel="canonical"]').length === 0) {
    const el = document.createElement("link");
    el.rel = "canonical";
    el.href = `${SITE_URL}/`;
    document.head.appendChild(el);
  }
};

const JournalSeo = ({ post, isIndex }) => {
  useEffect(() => {
    if (isIndex) {
      const title = "The Momentum Journal · Reflections from Authentic Momentum";
      const description =
        "Blunt, reflective writings from Authentic Momentum on clarity, energy, identity, resistance, alignment, and the shift from force to flow.";
      const url = `${SITE_URL}/journal`;
      document.title = title;
      upsertMeta('meta[name="description"]', { name: "description", content: description });
      upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
      upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
      upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
      upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
      upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
      upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
      upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
      upsertLink("canonical", url);
      injectJsonLd("breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "Journal", item: url },
        ],
      });
      return cleanup;
    }
    if (!post) return undefined;
    const url = `${SITE_URL}/journal/${post.slug}`;
    document.title = post.metaTitle;
    upsertMeta('meta[name="description"]', { name: "description", content: post.metaDescription });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: post.ogTitle });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: post.ogDescription });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "article" });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    upsertMeta('meta[property="article:published_time"]', {
      property: "article:published_time",
      content: post.publishedAt,
    });
    upsertMeta('meta[property="article:modified_time"]', {
      property: "article:modified_time",
      content: post.updatedAt,
    });
    upsertMeta('meta[property="article:author"]', { property: "article:author", content: "Cyrus Prescod" });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: post.ogTitle });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: post.ogDescription });
    upsertLink("canonical", url);

    injectJsonLd("article", {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.metaDescription,
      datePublished: post.publishedAt,
      dateModified: post.updatedAt,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      author: AUTHOR,
      publisher: { ...ORGANIZATION },
      keywords: post.themeTags.join(", "),
      articleSection: post.themeTags[0],
      url,
      inLanguage: "en",
      isPartOf: {
        "@type": "Blog",
        "@id": `${SITE_URL}/journal#blog`,
        name: "The Momentum Journal",
        url: `${SITE_URL}/journal`,
      },
    });

    if (post.faqs && post.faqs.length > 0) {
      injectJsonLd("faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }

    injectJsonLd("breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE_URL}/journal` },
        { "@type": "ListItem", position: 3, name: post.title, item: url },
      ],
    });

    return cleanup;
  }, [post, isIndex]);

  return null;
};

export default JournalSeo;
